
import { useEffect, useContext, Suspense, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { store } from "@/blyn/legacy/state/store";
import { loadOnboarding } from "@/blyn/services/onboardingService";
import { useSettingsContext } from "@/lib/settings";
import { ToastContext } from "@/lib/toast";
import { SessionContext } from "@/lib/auth";
import { NewProjectDialogProvider } from "@/blyn/legacy/modals/newProject";
import { ActionConfirmProvider } from "@/blyn/legacy/modals/ActionConfirmProvider";
import NavRail, { MobileNavList } from "@/blyn/NavRail";
import ProjectsRail from "@/blyn/ProjectsRail";
import CommandPalette from "@/blyn/CommandPalette";
import { BackgroundJobNotices } from "@/blyn/BackgroundJobNotices";
import { BlynIcon } from "@/blyn/BlynIcon";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import "@/blyn/blyn.css";

// Routes where the projects rail is redundant (the page itself is the projects browser)
const RAIL_HIDDEN_ROUTES = ["/blyn/projects"];

export default function BlynLayout() {
  const location = useLocation();
  const settings = useSettingsContext();
  const appToast = useContext(ToastContext);
  const { user: sessionUser } = useContext(SessionContext) as any;
  const showRail = !RAIL_HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));

  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  useEffect(() => {
    return store.select("mobileRailOpen", setMobileRailOpen);
  }, []);
  // Nav menu drawer only needs to be closed from within this component
  // (unlike mobileRailOpen, nothing deep inside ProjectsRail needs to
  // close it), so plain local state is enough — no global store entry.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Persist current route so MemoryRouter can restore it after a page refresh.
  // Includes location.search — dropping it would lose query params like
  // project-workspace's `?id=` on reload, landing back on an empty state.
  useEffect(() => {
    if (location.pathname === '/blyn/first-run') return;
    if (!location.pathname.startsWith('/blyn/')) return;
    sessionStorage.setItem('blyn-last-route', location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (store.get("onboardingPreviewMode")) return;

    if (sessionUser?.id || sessionUser?.userID || sessionUser?.userId) {
      const current = store.get("currentUser") as any;
      const tenantId = String(
        sessionUser.account_id ??
          sessionUser.accountId ??
          sessionUser.tenantId ??
          current?.account_id ??
          current?.tenantId ??
          "t_acme"
      );
      const userId = String(
        sessionUser.userID ?? sessionUser.userId ?? sessionUser.id ?? current?.id ?? "u_jdoe"
      );
      if (
        current?.id !== userId ||
        current?.tenantId !== tenantId ||
        String(current?.account_id ?? "") !== tenantId
      ) {
        store.set({
          currentUser: {
            id: userId,
            userID: userId,
            account_id: tenantId,
            firstName: sessionUser.firstname ?? sessionUser.firstName ?? "",
            lastName: sessionUser.lastname ?? sessionUser.lastName ?? "",
            initial: (sessionUser.firstname ?? sessionUser.firstName ?? "U")[0].toUpperCase(),
            role: "user",
            tenantId,
          },
        });
        loadOnboarding({ quiet: true, force: true });
        return;
      }
    }
    loadOnboarding({ quiet: true });
  }, [
    sessionUser?.id,
    sessionUser?.userID,
    sessionUser?.userId,
    sessionUser?.tenantId,
    sessionUser?.account_id,
    sessionUser?.accountId,
  ]);

  useEffect(() => {
    import("@/blyn/legacy/components/toast").then(({ installToastBridge, clearToastBridge }) => {
      installToastBridge((kind: string, title: string, sub?: string) => {
        const msg = sub || "";
        if (kind === "success") appToast.success(msg, title);
        else if (kind === "warn") appToast.warning(msg, title);
        else if (kind === "danger") appToast.error(msg, title);
        else appToast.info(msg, title);
      });
      return () => clearToastBridge();
    });
  }, [appToast]);

  useEffect(() => {
    const theme = settings.colorScheme || "dark";
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.add("ready");

    if (store.get("theme") !== theme) {
      store.set({ theme });
    }
  }, [settings.colorScheme]);

  useEffect(() => {
    const syncMode = (mode: string) => {
      document.documentElement.setAttribute("data-mode", mode || "simple");
    };

    const unsubscribeMode = store.select("mode", syncMode);

    return () => {
      unsubscribeMode?.();
    };
  }, []);

  useEffect(() => {
    const handleThemeChangeRequest = (event: Event) => {
      const requestedTheme = (event as CustomEvent<{ theme?: string }>).detail?.theme;
      if (requestedTheme !== "light" && requestedTheme !== "dark") return;

      settings.onUpdateField("colorScheme", requestedTheme);
    };

    window.addEventListener("blyn:theme-change-request", handleThemeChangeRequest);
    return () => {
      window.removeEventListener("blyn:theme-change-request", handleThemeChangeRequest);
    };
  }, [settings]);

  return (
    <NewProjectDialogProvider>
      <ActionConfirmProvider />
      <div
        id="blyn-app"
        className="blyn-app"
        style={showRail ? undefined : { gridTemplateColumns: 'var(--blyn-nav-w, 56px) 1fr' }}
      >
        <NavRail />
        {/* Below 1100px both the nav rail and the projects rail hide (see
            responsive.css) — these two floating buttons are the only
            persistent way to reach either, so they're kept outside
            .nav-rail/.projects-rail and always rendered regardless of
            route. Two separate toggles/drawers, not one combined menu —
            nav destinations and the current project's context are
            different concerns and shouldn't be stacked in one scroll. */}
        <IconButton
          className="blyn-mobile-nav-toggle"
          aria-label="Menu"
          onClick={() => setMobileNavOpen(v => !v)}
        >
          <BlynIcon name="menu" size={18} />
        </IconButton>
        <Drawer
          anchor="left"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          sx={{
            display: 'block',
            '@media (min-width: 1100px)': { display: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260, bgcolor: 'var(--bg-canvas)' },
          }}
        >
          <MobileNavList onNavigate={() => setMobileNavOpen(false)} />
        </Drawer>
        {showRail && (
          <>
            <IconButton
              className="blyn-mobile-menu-toggle"
              aria-label="Project Menu"
              onClick={() => store.set({ mobileRailOpen: !mobileRailOpen })}
            >
              <BlynIcon name="folder" size={18} />
            </IconButton>
            <Drawer
              anchor="left"
              open={mobileRailOpen}
              onClose={() => store.set({ mobileRailOpen: false })}
              sx={{
                display: 'block',
                '@media (min-width: 1100px)': { display: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, bgcolor: 'var(--bg-canvas)' },
                '& .projects-rail': { display: 'flex !important', height: '100%' }
              }}
            >
              <ProjectsRail />
            </Drawer>
            <div className="desktop-projects-rail" style={{ display: 'contents' }}>
              <ProjectsRail />
            </div>
          </>
        )}
        <main>
          <Suspense fallback={
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "transparent", pointerEvents: "none" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid transparent", borderTopColor: "var(--accent-text)", animation: "spin 0.7s linear infinite", opacity: 0.7 }} />
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>
        <CommandPalette />
        <BackgroundJobNotices />
      </div>
    </NewProjectDialogProvider>
  );
}
